import React, { useEffect, useState } from "react"
import { roundCurrency } from "../utils/costsUtils"
import { Box, Grid } from "@mui/material"
import PropTypes from "prop-types"
import ProjectsColumn from "./ProjectsColumn"
import {
  useProject,
  usePrefetchProjects,
} from "../../hooks/useOrganizationExpenses"
import ExpensesColumn from "./ExpensesColumn"
import ActionsColumn from "./ActionsColumn"
import { useQueryClient } from "@tanstack/react-query"
import {
  annotateChapters as utilsAnnotateChapters,
  getAllChapterPartidaAndDecompIds,
  getAllSubchapterPlainIds,
  getPartidaAndDecompIds,
  collectPartidaIdsFromChapter,
} from "../utils/costsUtils"

const CostsInputationGrid = ({
  projectsHeaders,
  allocation,
  setAllocation,
  formData,
  partidas,
  handleInputChange,
  onRemainingAmountChange,
}) => {
  const [projectsData, setProjectsData] = useState([])
  const queryClient = useQueryClient()

  const annotateChapters = utilsAnnotateChapters
  const [expandedProjectId, setExpandedProjectId] = useState(() => {
    if (projectsHeaders && projectsHeaders.length === 1) {
      return projectsHeaders[0].projectId
    }
    return null
  })

  useEffect(() => {
    if (projectsHeaders && projectsHeaders.length === 1) {
      setExpandedProjectId(projectsHeaders[0].projectId)
    }
  }, [projectsHeaders])

  const projectIdsFromPartidas = Array.isArray(partidas)
    ? Array.from(new Set(partidas.map(p => p.projectId).filter(Boolean)))
    : []
  usePrefetchProjects(projectIdsFromPartidas)

  const [selectedItems, setSelectedItems] = useState({})

  useEffect(() => {
    if (!Array.isArray(partidas) || partidas.length === 0) return
    const initial = {}
    partidas.forEach(p => {
      if (p && p.partidaId) {
        initial[p.partidaId] = true
        if (Array.isArray(p.decompositions) && p.decompositions.length > 0) {
          p.decompositions.forEach((val, idx) => {
            const num = Number(val)
            if (val != null && val !== "" && !Number.isNaN(num) && num !== 0) {
              initial[`${p.partidaId}_${idx}`] = true
            }
          })
        }
      }
    })
    setSelectedItems(prev => ({ ...initial, ...prev }))
  }, [partidas])

  useEffect(() => {
    if (!Array.isArray(partidas) || partidas.length === 0) return
    setAllocation(prev => {
      const newAlloc = { ...prev }
      partidas.forEach(p => {
        if (!p || !p.partidaId) return
        const pid = p.partidaId
        const existing = prev && prev[pid]

        const hasExistingAllocated =
          existing &&
          (typeof existing.allocated === "number" ||
            Array.isArray(existing.decomposition))
        if (hasExistingAllocated) return

        if (Array.isArray(p.decompositions) && p.decompositions.length > 0) {
          const decomps = p.decompositions.map(v => {
            if (typeof v === "number") return v
            const num = Number(v)
            return Number.isNaN(num) ? 0 : num
          })
          const sum = decomps.reduce(
            (s, n) => s + (typeof n === "number" ? n : 0),
            0,
          )
          // Prefer partida.allocated when provided, otherwise use decomposition sum
          const initialAllocated =
            typeof p.allocated === "number" ? p.allocated : sum
          newAlloc[pid] = {
            ...(newAlloc[pid] || {}),
            decomposition: decomps,
            allocated: initialAllocated,
          }
        } else if (typeof p.allocated === "number") {
          newAlloc[pid] = {
            ...(newAlloc[pid] || {}),
            allocated: p.allocated,
          }
        }
      })
      return newAlloc
    })
  }, [partidas, setAllocation])

  useEffect(() => {
    if (!Array.isArray(partidas) || partidas.length === 0) return
    if (!Array.isArray(projectsData) || projectsData.length === 0) return

    setSelectedItems(prev => {
      const newSel = { ...prev }
      partidas.forEach(p => {
        if (!p || !p.partidaId) return
        newSel[p.partidaId] = true

        for (const proj of projectsData) {
          const findInChapters = chapters => {
            if (!Array.isArray(chapters)) return null
            for (const ch of chapters) {
              if (Array.isArray(ch.partidas)) {
                const found = ch.partidas.find(
                  pp => String(pp.partidaId) === String(p.partidaId),
                )
                if (found) return found
              }
              if (Array.isArray(ch.chapters)) {
                const res = findInChapters(ch.chapters)
                if (res) return res
              }
            }
            return null
          }
          const projectPart = findInChapters(proj.chapters)
          if (projectPart) {
            const decompLen = Array.isArray(projectPart.decomposition)
              ? projectPart.decomposition.length
              : 0
            if (
              Array.isArray(p.decompositions) &&
              p.decompositions.length > 0
            ) {
              // mark decomposition indices that exist in the project definition
              p.decompositions.forEach((val, idx) => {
                const num = Number(val)
                if (
                  idx < decompLen &&
                  val != null &&
                  val !== "" &&
                  !Number.isNaN(num) &&
                  num !== 0
                ) {
                  newSel[`${p.partidaId}_${idx}`] = true
                }
              })
            }
            break
          }
        }
      })
      return newSel
    })
  }, [projectsData, partidas])
  const [expandedAccordions, setExpandedAccordions] = useState({})

  // Ensure parent chapters (including subchapters) show as selected when all their children are selected
  useEffect(() => {
    if (!Array.isArray(projectsData) || projectsData.length === 0) return
    // work on a copy
    let newSelected = { ...selectedItems }

    const computeForChapter = chapter => {
      if (Array.isArray(chapter.chapters)) {
        chapter.chapters.forEach(sub => computeForChapter(sub))
      }
      let allChildrenSelected = true

      if (Array.isArray(chapter.chapters)) {
        for (const sub of chapter.chapters) {
          if (!newSelected[sub.chapterId]) {
            allChildrenSelected = false
            break
          }
        }
      }

      if (allChildrenSelected && Array.isArray(chapter.partidas)) {
        for (const part of chapter.partidas) {
          if (newSelected[part.partidaId]) continue

          if (
            Array.isArray(part.decomposition) &&
            part.decomposition.length > 0
          ) {
            const allDecomps = part.decomposition.every(
              (_, idx) => !!newSelected[`${part.partidaId}_${idx}`],
            )
            if (!allDecomps) {
              allChildrenSelected = false
              break
            }
          } else {
            allChildrenSelected = false
            break
          }
        }
      }

      if (allChildrenSelected) {
        newSelected[chapter.chapterId] = true
      } else {
        if (newSelected[chapter.chapterId])
          delete newSelected[chapter.chapterId]
      }
    }

    projectsData.forEach(proj => {
      if (Array.isArray(proj.chapters)) {
        proj.chapters.forEach(ch => computeForChapter(ch))
      }
    })

    const keysA = Object.keys(selectedItems).sort()
    const keysB = Object.keys(newSelected).sort()
    const same =
      keysA.length === keysB.length && keysA.every((k, i) => k === keysB[i])
    if (!same) {
      setSelectedItems(newSelected)
    }
  }, [projectsData, selectedItems])

  const handleProjectExpand = projectId => {
    if (projectsData.some(p => p.projectId === projectId)) return
    setExpandedProjectId(projectId)
  }

  useEffect(() => {
    if (
      !Array.isArray(projectIdsFromPartidas) ||
      projectIdsFromPartidas.length === 0
    )
      return

    projectIdsFromPartidas.forEach(projectId => {
      const cached = queryClient.getQueryData(["projects", projectId])
      if (!cached || !Array.isArray(cached) || cached.length === 0) return
      const project = cached[0]
      const annotatedProject = {
        ...project,
        chapters: annotateChapters(project.chapters || [], project.projectId),
      }

      setProjectsData(prev => {
        if (prev.some(p => p.projectId === annotatedProject.projectId))
          return prev
        return [...prev, annotatedProject]
      })
    })
  }, [projectIdsFromPartidas, queryClient])

  const {
    data: fetchedProject,
    isSuccess,
    isLoading,
  } = useProject(expandedProjectId)

  useEffect(() => {
    if (
      isSuccess &&
      fetchedProject &&
      Array.isArray(fetchedProject) &&
      fetchedProject.length > 0
    ) {
      const project = fetchedProject[0]
      const annotatedProject = {
        ...project,
        chapters: annotateChapters(project.chapters || [], project.projectId),
      }

      setProjectsData(prev => {
        if (prev.some(p => p.projectId === annotatedProject.projectId))
          return prev
        const newProjects = [...prev, annotatedProject]
        console.log("projectsData:", newProjects)
        return newProjects
      })
    }
  }, [fetchedProject, isSuccess])

  // utils: getAllChapterPartidaAndDecompIds, getAllSubchapterPlainIds, getPartidaAndDecompIds

  const handleSelect = (type, item, parentChapters = []) => {
    setSelectedItems(prev => {
      let newSelected = { ...prev }

      const updateParentSelection = chapterOrPartida => {
        if (chapterOrPartida.chapterId) {
          let childIds = []
          if (Array.isArray(chapterOrPartida.chapters)) {
            childIds = childIds.concat(
              chapterOrPartida.chapters.map(sub => sub.chapterId),
            )
          }
          if (Array.isArray(chapterOrPartida.partidas)) {
            childIds = childIds.concat(
              chapterOrPartida.partidas.map(partida => partida.partidaId),
            )
          }
          if (childIds.length === 0) return
          const allChildrenSelected = childIds.every(id => newSelected[id])
          if (allChildrenSelected) {
            newSelected[chapterOrPartida.chapterId] = true
          } else {
            delete newSelected[chapterOrPartida.chapterId]
          }
        } else if (chapterOrPartida.partidaId) {
          let decompIds = []
          if (Array.isArray(chapterOrPartida.decomposition)) {
            decompIds = chapterOrPartida.decomposition.map(
              (_, idx) => `${chapterOrPartida.partidaId}_${idx}`,
            )
          }
          if (decompIds.length === 0) return
          const allDecompsSelected = decompIds.every(id => newSelected[id])
          if (allDecompsSelected) {
            newSelected[chapterOrPartida.partidaId] = true
          } else {
            delete newSelected[chapterOrPartida.partidaId]
          }
        }
      }

      let affectedIds = []
      if (type === "chapter") {
        const partAndDecompIds = getAllChapterPartidaAndDecompIds(item).filter(
          id => !id.endsWith("_c"),
        )
        const subchapterIds = getAllSubchapterPlainIds(item)
        affectedIds = [...partAndDecompIds, ...subchapterIds]

        const allSelected = affectedIds.every(id => !!prev[id])
        if (allSelected) {
          affectedIds.forEach(id => {
            delete newSelected[id]
          })
        } else {
          affectedIds.forEach(id => {
            newSelected[id] = true
          })
        }

        ;[item, ...parentChapters].forEach(node => updateParentSelection(node))
      } else if (type === "partida") {
        affectedIds = getPartidaAndDecompIds(item)
        const allSelected = affectedIds.every(id => !!prev[id])
        if (allSelected) {
          affectedIds.forEach(id => {
            delete newSelected[id]
          })
        } else {
          affectedIds.forEach(id => {
            newSelected[id] = true
          })
        }
        parentChapters.forEach(node => updateParentSelection(node))
      } else if (type === "decomposition") {
        const id = `${item.partidaId}_${item.decompIdx}`
        affectedIds = [id]
        if (prev[id]) {
          delete newSelected[id]
        } else {
          newSelected[id] = true
        }
        parentChapters.forEach(node => updateParentSelection(node))
      }

      setAllocation(prevAlloc => {
        let newAlloc = { ...prevAlloc }
        affectedIds.forEach(id => {
          if (!id.endsWith("_c") && newAlloc[id]) {
            if (newAlloc[id].allocated !== undefined) {
              newAlloc[id] = { ...newAlloc[id], allocated: 0 }
            }
            if (Array.isArray(newAlloc[id].decomposition)) {
              newAlloc[id] = {
                ...newAlloc[id],
                decomposition: newAlloc[id].decomposition.map(() => 0),
              }
            }
          }
        })
        return newAlloc
      })

      return newSelected
    })
  }

  // utils: collectPartidaIdsFromChapter

  const partidaIds = projectsData.reduce((acc, project) => {
    if (Array.isArray(project.chapters)) {
      project.chapters.forEach(ch => {
        acc = acc.concat(collectPartidaIdsFromChapter(ch))
      })
    }
    return acc
  }, [])

  let allocatedAmountOnlyPartidas = roundCurrency(
    partidaIds.reduce((sum, pid) => {
      const item = allocation?.[pid]
      if (item && typeof item.allocated === "number")
        return sum + item.allocated
      return sum
    }, 0),
  )

  const [organizationSelected, setOrganizationSelected] = React.useState(false)
  const [organizationAmount, _setOrganizationAmount] = React.useState(0)

  // On mount, if there's a remaining amount between overall allocated and partidas allocated,
  // preselect organization and set its allocated amount in `allocation` so UI shows the org input selected.
  React.useEffect(() => {
    const partidasSum = Array.isArray(partidas)
      ? partidas.reduce((s, p) => s + (p?.allocated || 0), 0)
      : 0
    const totalAllocatedFromForm = Number(
      formData?.allocated ?? formData?.subtotal ?? 0,
    )

    // If `organization` key is already present in allocation (even with allocated: 0),
    // respect it and reflect its value in local state. Only prefill when the key is
    // completely missing (undefined) to avoid overwriting user/reset actions.
    if (
      allocation &&
      Object.prototype.hasOwnProperty.call(allocation, "organization")
    ) {
      const allocationOrg = allocation.organization?.allocated || 0
      setOrganizationSelected(!!allocationOrg)
      _setOrganizationAmount(allocationOrg)
      return
    }

    const remaining = totalAllocatedFromForm - partidasSum
    if (remaining > 0) {
      setOrganizationSelected(true)
      _setOrganizationAmount(remaining)
      // merge into allocation so inputs show the value
      setAllocation(prev => ({
        ...prev,
        organization: { allocated: remaining },
      }))
    }
    // run on mount and when partidas or formData change; do not react to allocation changes
  }, [partidas, formData, setAllocation])

  const setOrganizationAmount = val => {
    _setOrganizationAmount(val)
    setAllocation(prev => ({
      ...prev,
      organization: { allocated: val },
    }))
  }

  if (organizationSelected && allocation?.organization?.allocated) {
    allocatedAmountOnlyPartidas += allocation.organization.allocated
  }

  return (
    <Box sx={{ p: 1 }}>
      <Grid container spacing={3}>
        <ProjectsColumn
          projectsHeaders={projectsHeaders}
          onProjectExpand={handleProjectExpand}
          projectsData={projectsData}
          loadingProjectId={isLoading ? expandedProjectId : null}
          selectedItems={selectedItems}
          onSelect={handleSelect}
          initialExpandedProjectId={
            projectsHeaders && projectsHeaders.length === 1
              ? projectsHeaders[0].projectId
              : null
          }
          organizationSelected={organizationSelected}
          setOrganizationSelected={setOrganizationSelected}
        />
        <ExpensesColumn
          selectedItems={selectedItems}
          projectsData={projectsData}
          allocation={allocation}
          setAllocation={setAllocation}
          expandedAccordions={expandedAccordions}
          setExpandedAccordions={setExpandedAccordions}
          organizationSelected={organizationSelected}
          organizationAmount={organizationAmount}
          setOrganizationAmount={setOrganizationAmount}
          projectId={
            projectsHeaders && projectsHeaders.length === 1
              ? projectsHeaders[0].projectId
              : undefined
          }
        />
        <ActionsColumn
          formData={formData}
          allocatedAmount={allocatedAmountOnlyPartidas}
          remainingAmount={
            Number(formData.subtotal) - allocatedAmountOnlyPartidas
          }
          setAllocation={setAllocation}
          selectedItems={selectedItems}
          projectsData={projectsData}
          projectsHeaders={projectsHeaders}
          expandedAccordions={expandedAccordions}
          setExpandedAccordions={setExpandedAccordions}
          allocation={allocation}
          setOrganizationAmount={setOrganizationAmount}
        />
      </Grid>
    </Box>
  )
}

CostsInputationGrid.propTypes = {
  projectsHeaders: PropTypes.array.isRequired,
  partidas: PropTypes.array.isRequired,
  allocation: PropTypes.object.isRequired,
  setAllocation: PropTypes.func.isRequired,
  formData: PropTypes.object,
  handleInputChange: PropTypes.func,
  onRemainingAmountChange: PropTypes.func,
}

export default CostsInputationGrid
